/**
 * @file Construit le menu latéral du panel admin depuis le registre des sections.
 * @module packages.common.layouts.admin.config-navigation
 *
 * Le menu est entièrement piloté par config/adminSections (source unique de
 * vérité) : une entrée apparaît si l'utilisateur détient la section
 * correspondante (super admin = tout). L'entrée « Équipe » est réservée au
 * super admin et n'est pas une section attribuable.
 */

import {useMemo} from 'react';
// routes
import {paths} from 'packages/common/routes/paths';
// components
import Iconify from 'packages/common/components/iconify';
// accès
import {usePermissions} from 'packages/common/hooks/session';
import {ADMIN_SECTIONS, ADMIN_NAV_GROUPS} from 'packages/common/config/adminSections';

// ----------------------------------------------------------------------

export function useNavData() {
  const {hasAdminSection, canManageSection, isSuperAdmin} = usePermissions();

  const data = useMemo(() => {
    const menu = [];

    // Dashboard : accessible à tout utilisateur du panel
    menu.push({
      items: [
        {
          title: 'Dashboard',
          path: paths.admin.root,
          icon: <Iconify icon="clarity:home-line" />,
        },
      ],
    });

    // Groupes pilotés par le registre des sections
    ADMIN_NAV_GROUPS.forEach((group) => {
      const items = ADMIN_SECTIONS.filter((section) => section.group === group.key && hasAdminSection(section.key)).flatMap((section) =>
        section.navItems
            .filter((item) => item.mode !== 'manage' || canManageSection(section.key))
            .map((item) => ({
              title: item.title,
              path: item.path,
              icon: <Iconify icon={item.icon} />,
            })),
      );

      // Deux pages du groupe Gestion sont réservées au super admin et ne figurent
      // donc pas au registre des sections attribuables : SMS, qui montre la marge
      // (économie interne Brydge), et les quotas newsletter, qui agrègent la
      // consommation du compte Amazon et celle de TOUS les établissements. Les
      // rendre attribuables laisserait un collaborateur en portée « portefeuille »
      // lire des chiffres d'établissements qui ne sont pas les siens.
      if (group.key === 'gestion' && isSuperAdmin) {
        const pagesSuperAdmin = [
          {title: 'SMS', path: paths.admin.communications.sms, icon: <Iconify icon="mdi:message-processing-outline" />},
          {
            title: 'Quotas newsletter',
            path: paths.admin.communications.newsletterQuotas,
            icon: <Iconify icon="clarity:bar-chart-line" />,
          },
        ];
        const emailsIndex = items.findIndex((item) => item.path === paths.admin.communications.emails);
        if (emailsIndex >= 0) {
          items.splice(emailsIndex + 1, 0, ...pagesSuperAdmin);
        } else {
          items.push(...pagesSuperAdmin);
        }
      }

      if (items.length > 0) {
        menu.push({subheader: group.subheader, items});
      }
    });

    // Gestion de l'équipe : super admin uniquement
    if (isSuperAdmin) {
      menu.push({
        subheader: 'Administration',
        items: [
          {
            title: 'Équipe',
            path: paths.admin.team,
            icon: <Iconify icon="clarity:shield-line" />,
          },
          {
            title: 'Réglages plateforme',
            path: paths.admin.settings,
            icon: <Iconify icon="solar:settings-bold" />,
          },
        ],
      });
    }

    return menu;
  }, [hasAdminSection, canManageSection, isSuperAdmin]);

  return data;
}
